import React, { useEffect, useState } from 'react';
import { DonutChart } from './DonutChart';
import { get } from '@/features/shared/lib/api';
import { ChartSkeletonLoader } from '@/features/shared/ui/ChartSkeletonLoader';

export function CollaborationChart({
  data: externalData = null,
  loading: externalLoading = false,
  error: externalError = null,
  colors,
  journalId = null,
  type = 'collaboration',
}) {
  // API에서 데이터 가져오기 (externalData가 없을 때만)
  const [collaborationData, setCollaborationData] = useState(null);
  const [collaborationLoading, setCollaborationLoading] = useState(false);
  const [collaborationErr, setCollaborationErr] = useState(null);

  useEffect(() => {
    // externalData가 제공되면 API 호출하지 않음
    if (externalData !== null) {
      return;
    }

    // journalId가 없으면 API 호출하지 않음
    if (!journalId) {
      return;
    }

    let alive = true;
    async function fetchCollaboration() {
      setCollaborationLoading(true);
      setCollaborationErr(null);
      try {
        const endpoint = type === 'academic_corporate'
          ? `/rm/v1/journal/${encodeURIComponent(journalId)}/academic_corporate_collaboration`
          : `/rm/v1/journal/${encodeURIComponent(journalId)}/collaboration`;
        const response = await get(endpoint);
        if (alive) {
          if (response?.status && response?.result) {
            setCollaborationData(response.result);
          } else {
            setCollaborationErr(response?.message || '데이터를 가져오는데 실패했습니다.');
            setCollaborationData(null);
          }
        }
      } catch (e) {
        if (alive) {
          setCollaborationErr(e?.response?.data?.message || e.message);
        }
      } finally {
        if (alive) {
          setCollaborationLoading(false);
        }
      }
    }
    fetchCollaboration();
    return () => { alive = false; };
  }, [externalData, journalId, type]);

  // 데이터 처리: externalData 또는 API 데이터 사용
  const data = externalData || collaborationData || [];
  const hasData = Array.isArray(data) && data.length > 0;
  const showLoading = !!externalLoading || (externalData === null && collaborationLoading);
  const showErr = externalError || (externalData === null ? collaborationErr : null);

  // Renders a donut chart with a right-below legend, reusable across pages
  return (
    <div className="relative min-h-[220px]">
      {hasData && (
        <>
          <div className="mb-4 min-h-[220px] flex items-center justify-center">
            <DonutChart title="" data={data} colors={colors} size={180} />
          </div>
          <div className="space-y-3 mt-2">
            {data.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between text-[#6C7687] text-[14px] mb-[4px] gap-2">
                <div className="flex items-center">
                  <span className="flex-shrink-0 inline-block w-[12px] h-[12px] rounded-full mr-[6px]" style={{ background: colors[idx % colors.length] }} />
                  <span className="text-[13px] text-[#6C7687]">{item.label}</span>
                </div>
                <div className="text-[#6C7687] text-[13px]">{typeof item.value === 'number' ? `${item.value}%` : item.value}</div>
              </div>
            ))}
          </div>
        </>
      )}
      {/* 로딩 상태 오버레이 */}
      <ChartSkeletonLoader show={showLoading} />
      {/* 에러 상태 오버레이 */}
      {showErr && !showLoading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/80 text-[#F3652D] text-sm">
          {showErr}
        </div>
      )}
      {/* 데이터 없음 오버레이 */}
      {!hasData && !showLoading && !showErr && (
        <div className="absolute inset-0 flex items-center justify-center text-[#8D9AAF]">
          데이터가 없습니다.
        </div>
      )}
    </div>
  );
}

export default CollaborationChart;
